import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo">
            <h1 className="logo-text">Osley & Associates</h1>
            <span className="logo-tagline">Custom Home Builders</span>
          </Link>
          
          <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
            <Link 
              to="/" 
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/projects" 
              className={`nav-link ${isActive('/projects') ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              Projects
            </Link>
            <Link 
              to="/team" 
              className={`nav-link ${isActive('/team') ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              Our Team
            </Link>
            <Link 
              to="/contact" 
              className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
          </nav>

          <button 
            className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
      
      <style jsx>{`
        .header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          border-bottom: 1px solid rgba(217, 119, 6, 0.1);
        }

        .header.scrolled {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(25px);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15);
          padding: 5px 0;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 0;
          transition: padding 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .header.scrolled .header-content {
          padding: 12px 0;
        }

        .logo {
          text-decoration: none;
          color: var(--primary-blue);
          transition: all 0.3s ease;
          position: relative;
        }

        .logo::before {
          content: '';
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          background: linear-gradient(135deg, var(--gold-accent), var(--light-gold));
          border-radius: 12px;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: -1;
        }

        .logo:hover::before {
          opacity: 0.1;
        }

        .logo:hover {
          transform: translateY(-2px);
        }

        .logo-text {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 2px;
          font-family: 'Playfair Display', serif;
          background: linear-gradient(135deg, var(--primary-blue) 0%, var(--gold-accent) 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          transition: all 0.3s ease;
        }

        .logo-tagline {
          font-size: 0.875rem;
          color: var(--gold-accent);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.3s ease;
        }

        .logo:hover .logo-tagline {
          letter-spacing: 2px;
          color: var(--light-gold);
        }

        .nav {
          display: flex;
          gap: 40px;
          align-items: center;
        }

        .nav-link {
          text-decoration: none;
          color: var(--text-dark);
          font-weight: 500;
          font-size: 1rem;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          padding: 8px 0;
          overflow: hidden;
        }

        .nav-link::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--gold-accent), var(--light-gold));
          border-radius: 8px;
          opacity: 0;
          transform: translateY(100%);
          transition: all 0.3s ease;
          z-index: -1;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 3px;
          background: linear-gradient(135deg, var(--gold-accent), var(--light-gold));
          transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 2px;
        }

        .nav-link:hover::before {
          opacity: 0.1;
          transform: translateY(0);
        }

        .nav-link:hover::after,
        .nav-link.active::after {
          width: 100%;
        }

        .nav-link:hover,
        .nav-link.active {
          color: var(--gold-accent);
          transform: translateY(-2px);
        }

        .nav-link.active {
          font-weight: 600;
        }

        .menu-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.3s ease;
          position: relative;
        }

        .menu-toggle:hover {
          background: rgba(217, 119, 6, 0.1);
        }

        .menu-toggle span {
          width: 25px;
          height: 3px;
          background: var(--primary-blue);
          border-radius: 2px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform-origin: center;
        }

        .menu-toggle.active span:nth-child(1) {
          transform: rotate(45deg) translate(6px, 6px);
          background: var(--gold-accent);
        }

        .menu-toggle.active span:nth-child(2) {
          opacity: 0;
          transform: scale(0);
        }

        .menu-toggle.active span:nth-child(3) {
          transform: rotate(-45deg) translate(6px, -6px);
          background: var(--gold-accent);
        }

        @media (max-width: 768px) {
          .nav {
            position: fixed;
            top: 85px;
            left: 0;
            right: 0;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(25px);
            flex-direction: column;
            padding: 40px 0;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
            border-top: 1px solid rgba(217, 119, 6, 0.1);
            transform: translateY(-100vh);
            transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
            gap: 30px;
          }

          .nav-open {
            transform: translateY(0);
          }

          .menu-toggle {
            display: flex;
          }

          .nav-link {
            padding: 12px 0;
            font-size: 1.125rem;
            position: relative;
          }

          .nav-link::before {
            display: none;
          }

          .nav-link::after {
            bottom: 0;
            height: 2px;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;